import { HttpResponse } from '@angular/common/http';
import { NgHttpCachingHeadersList, NgHttpCachingService } from 'ng-http-caching';
import { asyncScheduler, finalize, of, scheduled, shareReplay, tap } from 'rxjs';
import { ProtocolDistributor } from '@core/shared/protocol';
import { ConfigService } from '@core/shared/config';
import { CLIENT_CACHE_PROTOCOL, ClientCacheCustomHeaders } from '../client-cache';
import { randomString } from '@core/shared/utils';
import * as i0 from "@angular/core";
import * as i1 from "ng-http-caching";
import * as i2 from "@core/shared/config";
import * as i3 from "@core/shared/protocol";
const CLIENTCACHE_ID_HEADER = "x-cc-id";
function assignRequestId(req) {
  if (req.headers.get(CLIENTCACHE_ID_HEADER)) {
    return req;
  }
  const id = randomString(16);
  req = req.clone({
    headers: req.headers.append(CLIENTCACHE_ID_HEADER, id)
  });
  return req;
}
function getRequestId(req) {
  return req.headers.get(CLIENTCACHE_ID_HEADER) || null;
}
// a custom version of NgHttpCachingInterceptorService, modified to better understand / log state
export let ClientCacheInterceptor = /*#__PURE__*/(() => {
  class ClientCacheInterceptor {
    constructor(cacheService2, protocol, config) {
      this.cacheService2 = cacheService2;
      this.protocol = protocol;
      this.config = config;
      this.enableClientCache = !!this.config.get('clientCache.enable', 1);
      if (!this.enableClientCache) {
        this.protocol.write("ClientCacheInterceptor created (ClientCache disabled by configuration!)");
      } else {
        this.protocol.write("ClientCacheInterceptor created (ClientCache is enabled)");
      }
    }
    writeProtocol(req, message) {
      const id = getRequestId(req);
      this.protocol.writeToGroup(id, message);
    }
    intercept(req, next) {
      req = assignRequestId(req);
      this.writeProtocol(req, "handle request to " + req.url);
      // handle wiping + garbage collection
      this.cacheService2.runGc();
      if (req.headers.get(ClientCacheCustomHeaders.WIPE_CACHE) === '1') {
        this.writeProtocol(req, "pre-request cache wipe was requested");
        const key = this.cacheService2.getKey(req);
        this.cacheService2.clearCacheByKey(key);
      }
      // skip cache if disabled or uncacheable
      if (!this.enableClientCache || !this.cacheService2.isCacheable(req)) {
        this.writeProtocol(req, "request is uncacheable or cache disabled");
        return this.sendRequest(req, next);
      }
      // request can be cached, check how to handle
      this.writeProtocol(req, "intercept request - " + req.url /*+' headers: '+JSON.stringify(this.collectRequestHeaders(req))*/);
      const shared = this.processCacheableRequest(req, next);
      return shared.pipe(tap(() => {
        this.writeProtocol(req, "completed request - " + req.url);
      }));
    }
    processCacheableRequest(req, next) {
      // if there is a duplicate pending request, connect to it
      const cachedObservable = this.cacheService2.getFromQueue(req);
      if (cachedObservable) {
        this.writeProtocol(req, `Use data from already running parallel request`);
        return cachedObservable;
      }
      // existing cache entry? -> use it
      const cachedResponse = this.cacheService2.getFromCache(req);
      if (cachedResponse) {
        this.writeProtocol(req, `Use data from ClientCache`);
        return scheduled(of(cachedResponse.clone()), asyncScheduler);
      }
      // send request and add response to cache
      this.writeProtocol(req, `Send request and add to ClientCache`);
      const shared = this.sendRequest(req, next).pipe(tap(event => {
        if (event instanceof HttpResponse) {
          this.cacheService2.addToCache(req, event.clone());
        }
      }), finalize(() => {
        // delete pending request
        this.cacheService2.deleteFromQueue(req);
      }), shareReplay());
      // add pending request to queue for cache parallel request
      this.cacheService2.addToQueue(req, shared);
      return shared;
    }
    /**
     * Send http request (next handler)
     */
    sendRequest(req, next) {
      let cloned = req.clone();
      // trim custom headers before send request
      NgHttpCachingHeadersList.forEach(ngHttpCachingHeaders => {
        if (cloned.headers.has(ngHttpCachingHeaders)) {
          cloned = cloned.clone({
            headers: cloned.headers.delete(ngHttpCachingHeaders)
          });
        }
      });
      return next.handle(cloned);
    }
    collectRequestHeaders(req) {
      return req.headers.keys().reduce((headers, key) => {
        headers[key] = req.headers.get(key);
        return headers;
      }, {});
    }
    static {
      this.ɵfac = function ClientCacheInterceptor_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || ClientCacheInterceptor)(i0.ɵɵinject(i1.NgHttpCachingService), i0.ɵɵinject(CLIENT_CACHE_PROTOCOL), i0.ɵɵinject(i2.ConfigService));
      };
    }
    static {
      this.ɵprov = /*@__PURE__*/i0.ɵɵdefineInjectable({
        token: ClientCacheInterceptor,
        factory: ClientCacheInterceptor.ɵfac
      });
    }
  }
  return ClientCacheInterceptor;
})();